/** Run `fn` over `items` with at most `limit` in flight. Never rejects: a failing
 *  item is `fn`'s own business, so one bad video cannot abort the batch. */
export async function runPool<T>(items: T[], limit: number, fn: (item: T) => Promise<void>) {
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (;;) {
      const index = cursor++;
      if (index >= items.length) return;
      try {
        await fn(items[index]);
      } catch {
        // fn is expected to record its own failure state
      }
    }
  });
  await Promise.all(workers);
}

export function safeFilename(title: string, ext: string) {
  const base = title.replace(/[\\/:*?"<>|]/g, '').trim().slice(0, 120) || 'download';
  return `${base}.${ext}`;
}

/** Try to save the file directly. The RapidAPI CDN may not send CORS headers, in
 *  which case the browser refuses to read the bytes and we fall back to opening
 *  the link in a tab. Returns which path was taken so the UI can explain itself. */
export async function saveFile(url: string, filename: string): Promise<'saved' | 'opened'> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    return 'saved';
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
    return 'opened';
  }
}

export const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));
